import { type SkuEnvironment, supportedCountries } from './types';
import {
  type Brand,
  brandAndCountryFromZone,
  type Zone,
} from '@seek/audience-zones';
import type { Country } from '@seek/chalice-types';
import chaliceCountryToAudienceZone from './utils/chaliceCountryToAudienceZone';

export type ExperimentToggleNames = string; // Placeholder type in place for future ExperimentToggleNames. eg. "useGlobalRecs".

interface ConfigForDeprecatedExperimentFeature {
  cookieDomain: string;
  experimentEnabled: boolean;
  experimentApiUrl: string;
  experimentsConfig: Record<
    ExperimentToggleNames,
    {
      id: string;
      name: string;
      overrideCookieName: string;
    }
  >;
}

export interface ZoneConfig {
  SHOW_JOBCARD_TEASER_IN_SPLITVIEW?: boolean;
  SHOW_SALARY_TRANSPARENCY?: boolean;
  SHOULD_OPEN_APPLY_IN_NEW_TAB?: boolean;
  STICK_APPLY_BUTTON_ON_DESKTOP?: boolean;
  COMPANY_PROFILE_ON_JDV?: boolean;
}

export type Config = ConfigForDeprecatedExperimentFeature & {
  analyticsExperimentId: string; // rely on experiment mechanism
  brand: Brand;
  zone: Zone;
  metricsIsEnabled: boolean;
  metricsUrl: string;
  profileSettingsUrl: string;
  raygunEnabled: boolean;
  searchResultsPageUrl: string;
  version?: string;
  raygunApiKey?: string;
  graphqlEndpoint?: string; // to override graphql endpoint in gql client
  zoneFeatures: ZoneConfig;
};

type ConfigByCountryAndSkuEnv = Record<Country, Record<SkuEnvironment, Config>>;

const defaultConfig: Pick<
  Config,
  | 'cookieDomain'
  | 'graphqlEndpoint'
  | 'profileSettingsUrl'
  | 'searchResultsPageUrl'
> = {
  cookieDomain: 'seek.com.au', // used by experiment only. delete please.
  profileSettingsUrl: '/settings',
  searchResultsPageUrl: '/jobs',
};

const sharedProdConfig: Omit<Config, 'brand' | 'zone'> = {
  ...defaultConfig,
  analyticsExperimentId: '7ECFDBF9-347F-476E-AD8E-8ED0A564D514',
  experimentEnabled: true,
  metricsIsEnabled: true,
  metricsUrl: 'https://discover-metrics.cloud.seek.com.au',
  raygunEnabled: true,
  experimentApiUrl: 'https://experiments.cloud.seek.com.au/participants',
  experimentsConfig: {},
  zoneFeatures: {},
};

const mockPort = '8082';
const stubServer = `http://localhost:${mockPort}`;
export const devConfig: Config = {
  ...defaultConfig,
  brand: 'seek' as Brand,
  zone: 'anz-1',
  graphqlEndpoint: `${stubServer}/graphql`,
  analyticsExperimentId: 'DF0165A6-8F7D-481F-8DB0-BA5586E98188',
  metricsIsEnabled: false,
  metricsUrl: '',
  raygunEnabled: false,
  experimentEnabled: true,
  experimentApiUrl: `${stubServer}/experiments`,
  experimentsConfig: {},
  zoneFeatures: {},
};

const newConfigForCountry = (
  country: Country,
): Record<SkuEnvironment, Config> => {
  const zone = chaliceCountryToAudienceZone(country) || 'anz-1';
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const zoneConfig = require(`./zone-configs/${zone}`).default;

  const newConfigForProdEnvironment = () => {
    const brandTuple = brandAndCountryFromZone(zone);
    if (!brandTuple) {
      throw new Error('invalid brand');
    }
    const [brand] = brandTuple;
    return {
      ...sharedProdConfig,
      brand,
      zone,
    };
  };

  return {
    production: { ...newConfigForProdEnvironment(), ...zoneConfig },
    staging: { ...devConfig, ...zoneConfig },
    development: { ...devConfig, ...zoneConfig },
  };
};

const config: ConfigByCountryAndSkuEnv = supportedCountries
  .map((country) => [country, newConfigForCountry(country)])
  .reduce(
    (acc, [k, v]) => ({
      ...acc,
      [k as string]: v,
    }),
    {} as ConfigByCountryAndSkuEnv,
  );

export default config;
